import React, { useState } from 'react';
import { 
  TrendingUp, TrendingDown, CheckCircle, AlertTriangle, 
  Activity, Clock, Shield, Briefcase, CheckSquare, X, ThumbsUp, GitPullRequest,
  User as UserIcon, Users, Zap, Target, Layers, BarChart3
} from 'lucide-react';
import { Panel, ValueBadge, PriorityAlerts, LABEL, formatStrategicTier, reliabilityLabel, PsychologicalPortrait } from './CandidateShared';

const MissionControlTab = ({ 
  priorityAlerts, 
  performance, 
  profile, 
  resilience_persona,
  personality_persona, 
  allTargetWards = [], // Ensure it defaults to an empty array
  theiaWardScores,
  development, 
  peer 
}) => {
  
  // --- STATE FOR TABS ---
  const [activePortfolioTab, setActivePortfolioTab] = useState('priority');

  const perfPrediction = performance.performance_prediction || {};
  const devList = development.map((d) => ({
    area: d.area,
    priority: d.priority || "Medium",
    impact: d.impact || null,
  }));
  const peerStrengths = (peer.common_strengths || []).slice(0, 6);

  // --- HELPER: Group Wards by Strategic Priority ---
  const groupedWards = (() => {
    const groups = {
      priority: [],
      opportunity: [],
      other: []
    };

    allTargetWards.forEach(ward => {
      const tier = (ward.artemis_strategic_tier || "").toUpperCase();
      const combinedScore = ward.combined_priority_score || 0;

      // 1. PRIORITY: High-stakes, Critical, or Explicit Priority Tiers
      if (tier.includes("TIER_1") || tier.includes("CRITICAL") || tier.includes("URGENT") || tier.includes("PRIORITY")) {
        groups.priority.push(ward);
      } 
      // 2. OPPORTUNITY: Gain targets or wards with strong winability/fit scores (>0.55)
      else if (tier.includes("TIER_2") || tier.includes("GAIN") || tier.includes("OPPORTUNITY") || combinedScore > 0.55) {
        groups.opportunity.push(ward);
      } 
      // 3. OTHER: Defensive, Long-Term, or Safe seats
      else {
        groups.other.push(ward);
      }
    });

    // Sort by combined priority score within groups to show the "most important" first
    const sorter = (a, b) => (b.combined_priority_score || 0) - (a.combined_priority_score || 0);
    groups.priority.sort(sorter);
    groups.opportunity.sort(sorter);
    groups.other.sort(sorter);

    return groups;
  })();

  // --- LOGIC: Analysis Reliability ---
  const calculateReliability = () => {
    const confidence = profile.confidence_score || 0;
    const completeness = profile.data_completeness || 0;
    const peerCount = peer.assessment_count || 0;
    const peerConsensus = peer.consensus_score || 0;
    const overallReliability = (confidence * 0.3) + (completeness * 0.3) + (peerConsensus * 0.4);

    if (overallReliability >= 0.8) return { level: "Very High Confidence", color: "text-green-400", icon: CheckCircle, guidance: "This analysis is based on comprehensive, high-quality data. Strategic recommendations can be implemented with confidence." };
    if (overallReliability >= 0.65) return { level: "High Confidence", color: "text-blue-400", icon: ThumbsUp, guidance: "Good data quality. Recommendations are reliable, though some areas may benefit from additional peer input." };
    if (overallReliability >= 0.5) return { level: "Moderate Confidence", color: "text-yellow-400", icon: AlertTriangle, guidance: "Adequate data for initial strategy. Consider gathering more peer assessments or completing additional survey sections." };
    return { level: "Limited Confidence", color: "text-red-400", icon: X, guidance: "Data quality is limited. Use these insights as preliminary only. Prioritize data collection before major strategic decisions." };
  };

  const reliability = calculateReliability();
  const ReliabilityIcon = reliability.icon;
  const reliabilityScore = ((profile.confidence_score * 0.3) + (profile.data_completeness * 0.3) + (peer.consensus_score * 0.4));

  // --- LOGIC: Strategic Scenarios ---
  const getScenario = () => {
    const highProbability = perfPrediction.probability_high_performance || 0;
    const poorProbability = perfPrediction.probability_poor_performance || 0;

    if (highProbability > 0.6) return {
      type: "High-Upside Candidate", color: "text-green-400", bg: "bg-green-900/20 border-green-500/30", icon: TrendingUp,
      strategy: "This candidate has strong potential for breakthrough performance. Invest in high-visibility, ambitious campaign strategies. They can handle stretch goals.",
      resource: "Allocate resources to maximize ceiling, not just floor. Consider competitive races."
    };
    if (highProbability < 0.3 && poorProbability > 0.3) return {
      type: "High-Risk Profile", color: "text-red-400", bg: "bg-red-900/20 border-red-500/30", icon: AlertTriangle,
      strategy: "This candidate has elevated risk of underperformance. Focus on defensive strategy: strong ground game, controlled messaging, avoid high-stakes debates.",
      resource: "Provide extensive coaching and support. Consider safer, lower-profile race positioning."
    };
    return {
      type: "Stable Performer", color: "text-blue-400", bg: "bg-blue-900/20 border-blue-500/30", icon: Activity,
      strategy: "This candidate shows predictable, consistent performance. Standard campaign strategy applies. Focus on steady execution and incremental gains.",
      resource: "Balanced resource allocation. This candidate is a reliable asset for standard deployment."
    };
  };
  const scenario = getScenario();
  const ScenarioIcon = scenario.icon;

  const personalityProfile = profile.personality_profile || {};
  const topPersonalityStrengths = Object.entries(personalityProfile).sort(([, scoreA], [, scoreB]) => scoreB - scoreA).slice(0, 3).map(([dimension, score]) => ({ label: dimension, qualifier: LABEL(score) }));
  
  const communicationProfile = profile.communication_style || {};
  const topCommunicationStrengths = Object.entries(communicationProfile).sort(([, scoreA], [, scoreB]) => scoreB - scoreA).slice(0, 3).map(([dimension, score]) => ({ label: dimension, qualifier: LABEL(score) }));

  // --- SUB-COMPONENT: High-Density Portfolio Row (MODERNIZED) ---
  const PortfolioRow = ({ ward }) => {
    // Data Extraction
    const approach = ward.approach || "Standard campaign strategy.";
    const persuadables = ward.bq_persuadable_voters || 0;
    const swingPool = ward.bq_swing_pool_size || 0;
    
    // Scoring Factors
    const scores = ward.scoring_factors || {};
    const winability = (scores.artemis_winability_score * 100).toFixed(0);
    const fit = (scores.minerva_candidate_compatibility * 100).toFixed(0);
    const strategicPriority = (scores.artemis_strategic_priority * 100).toFixed(0);
    const poolRatio = (scores.bq_targetable_pool_ratio * 100).toFixed(0);

    // Border Color Logic
    const tier = (ward.artemis_strategic_tier || "").toUpperCase();
    let borderColor = "border-slate-600";
    if (tier.includes("TIER_1") || tier.includes("CRITICAL")) borderColor = "border-red-500";
    else if (tier.includes("TIER_2") || tier.includes("URGENT")) borderColor = "border-orange-500";
    else if (tier.includes("GAIN")) borderColor = "border-amber-400";
    else if (tier.includes("DEFEND") || tier.includes("LONG_TERM")) borderColor = "border-blue-400";
    else if (tier.includes("SAFE")) borderColor = "border-green-500";

    return (
      <div className={`group flex flex-col space-y-3 p-4 bg-slate-800/40 rounded-r-xl border-l-[6px] ${borderColor} hover:bg-slate-800 transition-all duration-200 shadow-sm hover:shadow-md`}>
        
        {/* Header Row */}
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div>
            <h5 className="font-bold text-white text-base sm:text-lg leading-tight">{ward.ward_name}</h5>
            <div className="mt-1.5">
              <ValueBadge value={formatStrategicTier(ward.artemis_strategic_tier)} />
            </div>
          </div>
          
          {/* Enhanced Pool Stats */}
          <div className="flex items-center bg-slate-900/50 rounded-lg p-2 border border-slate-700/50">
             <div className="text-right px-2">
                <span className="text-[10px] uppercase text-slate-500 font-bold tracking-wider block">Persuadable</span>
                <span className="text-white font-mono font-bold text-sm">{persuadables.toLocaleString()}</span>
             </div>
             <div className="w-px h-6 bg-slate-700 mx-1"></div>
             <div className="text-left px-2">
                <span className="text-[10px] uppercase text-slate-500 font-bold tracking-wider block">Total Swing</span>
                <span className="text-slate-400 font-mono font-bold text-sm">{swingPool.toLocaleString()}</span>
             </div>
          </div>
        </div>

        {/* Clean Approach Text */}
        <div className="text-xs text-slate-300 border-t border-slate-700/50 pt-3 leading-relaxed">
          {approach}
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
          
          {/* Winability */}
          <div className="flex flex-col bg-slate-900/30 rounded p-1.5 border border-slate-700/30">
            <span className="text-[10px] uppercase text-slate-500 font-bold tracking-wider flex items-center mb-0.5">
              <TrendingUp size={10} className="mr-1" /> Win
            </span>
            <span className={`font-mono font-bold text-sm ${winability > 60 ? 'text-green-400' : winability < 30 ? 'text-red-400' : 'text-yellow-400'}`}>
              {winability}%
            </span>
          </div>

          {/* Candidate Fit */}
          <div className="flex flex-col bg-slate-900/30 rounded p-1.5 border border-slate-700/30">
            <span className="text-[10px] uppercase text-slate-500 font-bold tracking-wider flex items-center mb-0.5">
              <UserIcon size={10} className="mr-1" /> Fit
            </span>
            <span className={`font-mono font-bold text-sm ${fit > 70 ? 'text-green-400' : fit < 50 ? 'text-red-400' : 'text-blue-400'}`}>
              {fit}%
            </span>
          </div>

          {/* Strategic Priority */}
          <div className="flex flex-col bg-slate-900/30 rounded p-1.5 border border-slate-700/30">
            <span className="text-[10px] uppercase text-slate-500 font-bold tracking-wider flex items-center mb-0.5">
              <Target size={10} className="mr-1" /> Pri
            </span>
            <span className="font-mono font-bold text-sm text-white">
              {strategicPriority}
            </span>
          </div>

          {/* Efficiency Ratio */}
          <div className="flex flex-col bg-slate-900/30 rounded p-1.5 border border-slate-700/30">
            <span className="text-[10px] uppercase text-slate-500 font-bold tracking-wider flex items-center mb-0.5">
              <Layers size={10} className="mr-1" /> Eff
            </span>
            <span className="font-mono font-bold text-sm text-slate-300">
              {poolRatio}%
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
      {/* LEFT COLUMN */}
      <div className="lg:col-span-2 space-y-8">
        <PriorityAlerts alerts={priorityAlerts} />

        {/* --- STRATEGIC PORTFOLIO --- */}
        <Panel title="Strategic Portfolio Analysis" icon={GitPullRequest} collapsible={true} defaultOpen={true}>
            <p className="text-sm text-slate-400 mb-6">
              A data-driven breakdown of the ward map, prioritized by strategic necessity and candidate resonance.
            </p>
            
            {/* TABS HEADER */}
            <div className="flex border-b border-slate-700 mb-6 space-x-6 overflow-x-auto scrollbar-none">
                {[
                    { id: 'priority', label: 'Priority', labelFull: 'Priority Objectives', icon: AlertTriangle, count: groupedWards.priority.length, color: 'text-red-400' },
                    { id: 'opportunity', label: 'Opps', labelFull: 'Strategic Opps', icon: Zap, count: groupedWards.opportunity.length, color: 'text-amber-400' },
                    { id: 'other', label: 'Defensive', labelFull: 'Defensive / Long-Term', icon: Shield, count: groupedWards.other.length, color: 'text-blue-400' }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActivePortfolioTab(tab.id)}
                        className={`pb-3 text-xs md:text-sm font-bold uppercase tracking-wider flex items-center transition-all relative whitespace-nowrap flex-shrink-0 ${
                            activePortfolioTab === tab.id ? 'text-white' : 'text-slate-500 hover:text-slate-300'
                        }`}
                    >
                        <tab.icon size={14} className={`mr-2 ${activePortfolioTab === tab.id ? tab.color : 'text-slate-600'}`} />
                        <span className="sm:hidden">{tab.label}</span><span className="hidden sm:inline">{tab.labelFull}</span> <span className="ml-1 sm:ml-2 px-1.5 py-0.5 bg-slate-800 rounded-full text-[10px] text-slate-400">{tab.count}</span>
                        {activePortfolioTab === tab.id && (
                            <div className={`absolute bottom-0 left-0 right-0 h-[2px] ${tab.id === 'priority' ? 'bg-red-500' : tab.id === 'opportunity' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                        )}
                    </button>
                ))}
            </div>

            {/* TAB CONTENT */}
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                {activePortfolioTab === 'priority' && (
                    <div className="space-y-3">
                        {groupedWards.priority.length > 0 ? groupedWards.priority.map(ward => (
                            <PortfolioRow key={ward.ward_code} ward={ward} />
                        )) : <p className="text-slate-500 italic p-4">No priority objectives identified.</p>}
                    </div>
                )}

                {activePortfolioTab === 'opportunity' && (
                    <div className="space-y-3">
                        {groupedWards.opportunity.length > 0 ? groupedWards.opportunity.map(ward => (
                            <PortfolioRow key={ward.ward_code} ward={ward} />
                        )) : <p className="text-slate-500 italic p-4">No strategic opportunities identified.</p>}
                    </div>
                )}

                {activePortfolioTab === 'other' && (
                    <div className="space-y-3">
                        {groupedWards.other.length > 0 ? groupedWards.other.map(ward => (
                            <PortfolioRow key={ward.ward_code} ward={ward} />
                        )) : <p className="text-slate-500 italic p-4">No defensive or long-term targets identified.</p>}
                    </div>
                )}
            </div>
        </Panel>

        {/* ... Rest of the Component (Personality, Peer, etc.) Remains Unchanged ... */}
        <Panel title="Psychological & Strengths Profile" icon={Shield}>
             {personality_persona && (
                <div className="mb-6 bg-slate-700/30 p-4 rounded-lg border border-slate-700">
                  <h4 className="font-semibold text-white mb-2 flex items-center"><UserIcon size={16} className="mr-2 text-purple-400" /> Personality Persona</h4>
                  <p className="text-lg font-bold text-white">{personality_persona.persona_name || "Balanced"}</p>
                  <p className="text-sm text-slate-300 mt-1">{personality_persona.insight || "Candidate shows a balanced personality profile."}</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-3 bg-slate-700/30 p-4 rounded-lg border border-slate-700">
                  <h4 className="font-semibold text-white flex items-center"><CheckCircle size={16} className="mr-2 text-green-400"/> Top Personality Strengths</h4>
                  <ul className="space-y-2">
                    {topPersonalityStrengths.length > 0 ? topPersonalityStrengths.map((s, i) => (
                      <li key={i} className="text-sm"><strong className="text-white capitalize">{s.label.replace('_', ' ')}</strong><div className="mt-1"><ValueBadge value={s.qualifier} /></div></li>
                    )) : <li className="text-slate-400 text-sm">No personality data found.</li>}
                  </ul>
                </div>
                <div className="space-y-3 bg-slate-700/30 p-4 rounded-lg border border-slate-700">
                  <h4 className="font-semibold text-white flex items-center"><Activity size={16} className="mr-2 text-blue-400"/> Top Communication Styles</h4>
                  <ul className="space-y-2">
                    {topCommunicationStrengths.length > 0 ? topCommunicationStrengths.map((s, i) => (
                      <li key={i} className="text-sm"><strong className="text-white capitalize">{s.label.replace('_', ' ')}</strong><div className="mt-1"><ValueBadge value={s.qualifier} /></div></li>
                    )) : <li className="text-slate-400 text-sm">No communication data found.</li>}
                  </ul>
                </div>
              </div>
              <PsychologicalPortrait profile={profile.personality_profile} />
        </Panel>

        <Panel title="Peer Assessment" icon={Briefcase}>
            {peer.assessment_count > 0 && (
            <div className="mb-4 text-sm flex justify-between items-center bg-slate-700/30 p-3 rounded-lg">
                <span className="text-slate-300">Consensus from <strong className="text-white">{peer.assessment_count} peers</strong></span>
                <ValueBadge value={reliabilityLabel(peer.consensus_score)} type="CONSENSUS" />
            </div>
            )}
            
            <h4 className="font-semibold text-white mb-2">Common Strengths:</h4>
            <div className="flex flex-wrap gap-2 mb-4">
            {peerStrengths.length > 0 ? peerStrengths.map((s, i) => (
                <span key={i} className="px-2 py-1 bg-green-500/10 text-green-400 rounded-lg text-xs">{s}</span>
            )) : <span className="text-xs text-slate-400">N/A</span>}
            </div>
            
            <h4 className="font-semibold text-white mb-2">Development Areas:</h4>
            <div className="flex flex-wrap gap-2">
            {(peer.common_development_areas || []).length > 0 ? (peer.common_development_areas || []).map((d, i) => (
                <span key={i} className="px-2 py-1 bg-red-500/10 text-red-400 rounded-lg text-xs">{d}</span>
            )) : <span className="text-xs text-slate-400">N/A</span>}
            </div>
        </Panel>
      </div>

      {/* RIGHT COLUMN */}
      <aside className="space-y-8">
        {/* --- PANEL: Analysis Reliability (AESTHETIC & SCALING FIX) --- */}
<Panel title="Analysis Reliability" icon={Shield}>
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center space-x-2 min-w-0">
      <ReliabilityIcon size={18} className={`${reliability.color} shrink-0`} />
      <span className={`font-bold ${reliability.color} text-sm truncate`}>
        {reliability.level}
      </span>
    </div>
    {/* Robust Score: Prevents NaN/100 by defaulting to 0 */}
    <span className="text-xl font-bold text-white shrink-0 ml-2">
      {isNaN(reliabilityScore) ? 0 : (reliabilityScore * 100).toFixed(0)}
      <span className="text-xs text-slate-500 ml-1 font-medium">/100</span>
    </span>
  </div>

  <p className="text-xs text-slate-400 mb-6 leading-relaxed">
    {reliability.guidance}
  </p>

  {/* Metric Rows: Clean horizontal alignment with overflow protection */}
  <div className="space-y-3 pt-4 border-t border-slate-700/50">
    {[
      { 
        label: "Survey Quality", 
        val: profile.data_completeness > 0.8 ? 'Comprehensive' : profile.data_completeness > 0.6 ? 'Adequate' : 'Incomplete',
        color: profile.data_completeness > 0.8 ? 'text-green-400' : profile.data_completeness > 0.6 ? 'text-yellow-400' : 'text-red-400'
      },
      { 
        label: "Peer Validation", 
        val: peer.assessment_count >= 3 && (peer.consensus_score || 0) > 0.7 ? 'Strong' : peer.assessment_count >= 2 ? 'Moderate' : 'Limited',
        color: peer.assessment_count >= 3 && (peer.consensus_score || 0) > 0.7 ? 'text-green-400' : peer.assessment_count >= 2 ? 'text-yellow-400' : 'text-red-400'
      },
      { 
        label: "Consistency", 
        val: (profile.confidence_score || 0) > 0.75 ? 'Highly Stable' : (profile.confidence_score || 0) > 0.5 ? 'Generally Stable' : 'Variable',
        color: (profile.confidence_score || 0) > 0.75 ? 'text-green-400' : (profile.confidence_score || 0) > 0.5 ? 'text-yellow-400' : 'text-red-400'
      }
    ].map((metric, i) => (
      <div key={i} className="flex justify-between items-center text-[11px] font-medium min-w-0">
        <span className="text-slate-500 uppercase tracking-wider truncate mr-4">{metric.label}</span>
        <span className={`${metric.color} shrink-0 text-right`}>{metric.val}</span>
      </div>
    ))}
  </div>
</Panel>

        <Panel title="Performance & Resilience Profile" icon={Shield}>
            {resilience_persona && (
                <div className="mb-4 bg-slate-700/30 p-4 rounded-lg border border-slate-700">
                <h4 className="font-semibold text-white mb-2 flex items-center">
                    <Shield size={16} className="mr-2 text-purple-400" /> Resilience Persona
                </h4>
                <p className="text-lg font-bold text-white">{resilience_persona.resilience_archetype || "Balanced"}</p>
                {resilience_persona.primary_risk?.includes('WARNING') ? (
                    <div className="mt-2 flex items-start space-x-2 p-3 rounded-md bg-red-900/30 border border-red-500/30">
                    <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-300">{resilience_persona.primary_risk}</p>
                    </div>
                ) : (
                    <p className="text-sm text-slate-300 mt-1">{resilience_persona.primary_risk || "No significant resilience risks identified."}</p>
                )}
                </div>
            )}
            
            {/* --- PANEL: Performance Spectrum (AESTHETIC & SCALING FIX) --- */}
<div className="mb-4 bg-slate-700/30 p-4 rounded-lg border border-slate-700">
  <h4 className="font-semibold text-white text-xs uppercase tracking-widest mb-4 opacity-70">Performance Spectrum</h4>
  <div className="space-y-3">
    {[
      { label: "Expected", val: perfPrediction.expected_performance, icon: TrendingUp, color: "text-accent-400", bg: "bg-accent-500/10" },
      { label: "Optimistic", val: perfPrediction.maximum_likely, icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/10" },
      { label: "Pessimistic", val: perfPrediction.minimum_likely, icon: TrendingDown, color: "text-red-400", bg: "bg-red-500/10" }
    ].map((item, idx) => (
      <div key={idx} className="flex items-center p-2 rounded-md bg-slate-800/40 border border-slate-700/30 min-w-0">
        <div className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg ${item.bg} border border-white/5 mr-3`}>
          <item.icon size={18} className={item.color} />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-[10px] font-bold text-slate-500 uppercase block tracking-tighter leading-none mb-1">
            {item.label}
          </span>
          <strong className={`text-sm md:text-base font-bold truncate block ${item.color}`}>
            {item.val ? LABEL(item.val) : "Unknown"}
          </strong>
        </div>
      </div>
    ))}
  </div>
</div>

            <h4 className="font-semibold text-white mb-3 pt-4 border-t border-slate-700">Personal Resilience</h4>
            <ul className="space-y-4">
                  <li className="flex items-center justify-between"><span className="text-sm text-slate-300">Stress Composure</span><ValueBadge value={LABEL(profile.resilience_profile?.stress_composure)} /></li>
                  <li className="flex items-center justify-between"><span className="text-sm text-slate-300">Setback Recovery</span><ValueBadge value={LABEL(profile.resilience_profile?.setback_recovery)} /></li>
                  <li className="flex items-center justify-between"><span className="text-sm text-slate-300">Criticism Resilience</span><ValueBadge value={LABEL(profile.resilience_profile?.criticism_resilience)} /></li>
                  <li className="flex items-center justify-between"><span className="text-sm text-slate-300">Energy Sustainability</span><ValueBadge value={LABEL(profile.resilience_profile?.energy_sustainability)} /></li>
            </ul>

            {performance.risk_assessment?.volatility_risk && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                    <h5 className="font-semibold text-white mb-2">Performance Volatility Risk</h5>
                    <ValueBadge value={performance.risk_assessment.volatility_risk} />
                </div>
            )}
        </Panel>

        <Panel title="Candidate Development Priorities" icon={Clock}>
            <ul className="space-y-4">
            {devList.length > 0 ? devList.map((d, i) => {
                const isCorroborated = d.area === 'Message Adaptation' && (performance.improvement_opportunities || []).some(o => o.area === 'Message Adaptation');
                return (
                <li key={i} className="text-sm border-l-2 border-yellow-500 pl-3">
                    <div className="flex justify-between items-start">
                        <strong className="text-white">{d.area}</strong>
                        <ValueBadge value={d.priority} type="PRIORITY" />
                    </div>
                    {d.impact && <p className="text-slate-400 text-sm mt-1">Impact: {d.impact}</p>}
                    {isCorroborated && (
                    <div className="mt-2 flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium border bg-blue-500/10 text-blue-300 border-blue-500/20">
                        <CheckSquare size={14} /><span>Corroborated Insight</span>
                    </div>
                    )}
                </li>
                );
            }) : <li className="text-slate-400 text-sm">No immediate development priorities identified.</li>}
            </ul>
        </Panel>
      </aside>
    </div>
  );
};

export default MissionControlTab;